import "./LoginButtons.css";
import {
    LOGIN_WITH_FACEBOOK_URL,
    LOGIN_WITH_FACEBOOK_TOKEN_URL,
    LOGIN_WITH_GOOGLE_URL,
    LOGIN_WITH_GOOGLE_CODE_URL,
    LOGIN_WITH_GOOGLE_TOKEN_URL,
    loginWithAppleToken,
    loginWithFacebookToken,
    loginWithGoogleToken,
} from "../server/api";
import UiState from "../state/UiState";
import { DEBUG, ANDROID_APP_VERSION } from "config";
import * as Native from "@faintlines/native-bridge";

import React, { useState, useEffect, useCallback } from "react";
import classnames from "classnames";

// TODO: extract all state to Mobx

const LOGIN_FAILED_TEXT = "Login failed, please try again or contact support.";

export const LoginButtons = () =>
    DEBUG ? (
        <>
            <AppleLoginButton />
            <FacebookLoginButton />
            <GoogleLoginButton />
        </>
    ) : Native.HAVE_IOS ? (
        <>
            <AppleLoginButton />
            {/* <FacebookLoginButtonNG /> */}
            {/* <GoogleLoginButtonNG source="ios" /> */}
        </>
    ) : (
        <>
            <FacebookLoginButton />
            <GoogleLoginButton />
        </>
    );

export function FacebookLoginButton({ inline }) {
    const [loading, setLoading] = useState(false);

    const loginSuccessHandler = useCallback((evt) => {
        const nextUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LOGIN_WITH_FACEBOOK_TOKEN_URL}?token=${evt.detail.accessToken}&next=${nextUrl}`;
    }, []);

    const loginFailureHandler = useCallback(() => {
        setLoading(false);
        UiState.showAlert(LOGIN_FAILED_TEXT);
    }, []);

    const loginCancelledHandler = useCallback(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        window.addEventListener("fbloginsuccess", loginSuccessHandler);
        window.addEventListener("fblogincancel", loginCancelledHandler);
        window.addEventListener("fbloginerror", loginFailureHandler);
        return () => {
            window.removeEventListener("fbloginsuccess", loginSuccessHandler);
            window.removeEventListener("fblogincancel", loginCancelledHandler);
            window.removeEventListener("fbloginerror", loginFailureHandler);
        };
    }, [loginSuccessHandler, loginCancelledHandler, loginFailureHandler]);

    function clickHandler(evt) {
        setLoading(true);
        if (Native.CAPABILITIES.loginWithFacebook) {
            evt.preventDefault();
            Native.loginWithFacebook();
        }
    }

    const nextUrl = encodeURIComponent(window.location.href);
    return (
        <a
            className={classnames("LoginButton facebook", {
                inline,
                loading,
            })}
            href={`${LOGIN_WITH_FACEBOOK_URL}?next=${nextUrl}`}
            onClick={clickHandler}
        >
            <i className="LoginButton-icon icon-facebook" />
            {inline ? "Connect" : "Continue with Facebook"}
        </a>
    );
}

export function FacebookLoginButtonNG({ inline }) {
    const [loading, setLoading] = useState(false);

    const loginSuccessHandler = useCallback((evt) => {
        const { token } = evt.detail;
        loginWithFacebookToken(token)
            .then(() => {
                window.location.reload();
            })
            .catch(() => {
                UiState.showAlert(LOGIN_FAILED_TEXT);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const loginFailureHandler = useCallback(() => {
        setLoading(false);
        UiState.showAlert(LOGIN_FAILED_TEXT);
    }, []);

    const loginCancelledHandler = useCallback(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        window.addEventListener("fbloginsuccess", loginSuccessHandler);
        window.addEventListener("fblogincancel", loginCancelledHandler);
        window.addEventListener("fbloginerror", loginFailureHandler);
        return () => {
            window.removeEventListener("fbloginsuccess", loginSuccessHandler);
            window.removeEventListener("fblogincancel", loginCancelledHandler);
            window.removeEventListener("fbloginerror", loginFailureHandler);
        };
    }, [loginSuccessHandler, loginCancelledHandler, loginFailureHandler]);

    if (!Native.CAPABILITIES.loginWithFacebook && !DEBUG) {
        return null;
    }

    function clickHandler() {
        setLoading(true);
        Native.loginWithFacebook();
    }

    return (
        <div
            className={classnames("LoginButton facebook", {
                inline,
                loading,
            })}
            onClick={clickHandler}
        >
            <i className="LoginButton-icon icon-facebook" />
            {inline ? "Connect" : "Continue with Facebook"}
        </div>
    );
}

export function GoogleLoginButton({ inline }) {
    const [loading, setLoading] = useState(false);

    const loginSuccessHandler = useCallback((evt) => {
        const nextUrl = encodeURIComponent(window.location.href);
        const { authCode, idToken } = evt.detail;
        const loginUrl = authCode
            ? `${LOGIN_WITH_GOOGLE_CODE_URL}?code=${authCode}`
            : `${LOGIN_WITH_GOOGLE_TOKEN_URL}?token=${idToken}`;
        window.location.href = `${loginUrl}&next=${nextUrl}`;
    }, []);

    const loginFailureHandler = useCallback(() => {
        setLoading(false);
        UiState.showAlert(LOGIN_FAILED_TEXT);
    }, []);

    const loginCancelledHandler = useCallback(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        window.addEventListener("googleloginsuccess", loginSuccessHandler);
        window.addEventListener("googleloginerror", loginFailureHandler);
        window.addEventListener("googlelogincancelled", loginCancelledHandler);
        return () => {
            window.removeEventListener(
                "googleloginsuccess",
                loginSuccessHandler
            );
            window.removeEventListener("googleloginerror", loginFailureHandler);
            window.removeEventListener(
                "googlelogincancelled",
                loginCancelledHandler
            );
        };
    }, [loginSuccessHandler, loginFailureHandler, loginCancelledHandler]);

    if (Native.HAVE_ANDROID && ANDROID_APP_VERSION < 6) {
        return null;
    }

    function clickHandler(evt) {
        setLoading(true);
        if (Native.CAPABILITIES.loginWithGoogle) {
            evt.preventDefault();
            Native.loginWithGoogle();
        }
    }

    const nextUrl = encodeURIComponent(window.location.href);
    return (
        <a
            className={classnames("LoginButton google", { inline, loading })}
            href={`${LOGIN_WITH_GOOGLE_URL}?next=${nextUrl}`}
            onClick={clickHandler}
        >
            <i className="LoginButton-icon icon-google" />
            {inline ? "Connect" : "Continue with Google"}
        </a>
    );
}

export function GoogleLoginButtonNG({ inline, source }) {
    const [loading, setLoading] = useState(false);

    const loginSuccessHandler = useCallback(
        (evt) => {
            const { token } = evt.detail;
            loginWithGoogleToken(token, source)
                .then(() => {
                    window.location.reload();
                })
                .catch(() => {
                    UiState.showAlert(LOGIN_FAILED_TEXT);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [source]
    );

    const loginFailureHandler = useCallback(() => {
        setLoading(false);
        UiState.showAlert(LOGIN_FAILED_TEXT);
    }, []);

    const loginCancelledHandler = useCallback(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        window.addEventListener("googleloginsuccess", loginSuccessHandler);
        window.addEventListener("googleloginerror", loginFailureHandler);
        window.addEventListener("googlelogincancelled", loginCancelledHandler);
        return () => {
            window.removeEventListener(
                "googleloginsuccess",
                loginSuccessHandler
            );
            window.removeEventListener("googleloginerror", loginFailureHandler);
            window.removeEventListener(
                "googlelogincancelled",
                loginCancelledHandler
            );
        };
    }, [loginSuccessHandler, loginFailureHandler, loginCancelledHandler]);

    if (!Native.CAPABILITIES.loginWithGoogle && !DEBUG) {
        return null;
    }

    function clickHandler() {
        setLoading(true);
        Native.loginWithGoogle();
    }

    return (
        <div
            className={classnames("LoginButton google", { inline, loading })}
            onClick={clickHandler}
        >
            <i className="LoginButton-icon icon-google" />
            {inline ? "Connect" : "Continue with Google"}
        </div>
    );
}

export function AppleLoginButton({ inline }) {
    const [loading, setLoading] = useState(false);

    const loginSuccessHandler = useCallback((evt) => {
        const { token } = evt.detail;
        loginWithAppleToken(token)
            .then(() => {
                window.location.reload();
            })
            .catch(() => {
                UiState.showAlert(LOGIN_FAILED_TEXT);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const loginFailureHandler = useCallback(() => {
        setLoading(false);
        UiState.showAlert(LOGIN_FAILED_TEXT);
    }, []);

    const loginCancelledHandler = useCallback(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        window.addEventListener("apple_login_success", loginSuccessHandler);
        window.addEventListener("apple_login_error", loginFailureHandler);
        window.addEventListener("apple_login_cancelled", loginCancelledHandler);
        return () => {
            window.removeEventListener(
                "apple_login_success",
                loginSuccessHandler
            );
            window.removeEventListener(
                "apple_login_error",
                loginFailureHandler
            );
            window.removeEventListener(
                "apple_login_cancelled",
                loginCancelledHandler
            );
        };
    }, [loginSuccessHandler, loginFailureHandler, loginCancelledHandler]);

    if (!Native.CAPABILITIES.loginWithApple && !DEBUG) {
        return null;
    }

    function clickHandler() {
        setLoading(true);
        Native.loginWithApple();
    }

    return (
        <div
            className={classnames("LoginButton apple", { inline, loading })}
            onClick={clickHandler}
        >
            <IconApple />
            {inline ? "Connect" : "Continue with Apple"}
        </div>
    );
}

const IconApple = () => (
    <svg
        className="LoginButton-icon"
        width="16px"
        height="16px"
        viewBox="20 16 20 20"
    >
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <path
                d="M28.2226562,20.3846154 C29.0546875,20.3846154 30.0976562,19.8048315 30.71875,19.0317864 C31.28125,18.3312142 31.6914062,17.352829 31.6914062,16.3744437 C31.6914062,16.2415766 31.6796875,16.1087095 31.65625,16 C30.7304687,16.0362365 29.6171875,16.640178 28.9492187,17.4494596 C28.421875,18.06548 27.9414062,19.0317864 27.9414062,20.0222505 C27.9414062,20.1671964 27.9648438,20.3121424 27.9765625,20.3604577 C28.0351562,20.3725366 28.1289062,20.3846154 28.2226562,20.3846154 Z M25.2929688,35 C26.4296875,35 26.9335938,34.214876 28.3515625,34.214876 C29.7929688,34.214876 30.109375,34.9758423 31.375,34.9758423 C32.6171875,34.9758423 33.4492188,33.792117 34.234375,32.6325493 C35.1132812,31.3038779 35.4765625,29.9993643 35.5,29.9389701 C35.4179688,29.9148125 33.0390625,28.9122695 33.0390625,26.0979021 C33.0390625,23.6579784 34.9140625,22.5588048 35.0195312,22.474253 C33.7773438,20.6382708 31.890625,20.5899555 31.375,20.5899555 C29.9804688,20.5899555 28.84375,21.4596313 28.1289062,21.4596313 C27.3554688,21.4596313 26.3359375,20.6382708 25.1289062,20.6382708 C22.8320312,20.6382708 20.5,22.5950413 20.5,26.2911634 C20.5,28.5861411 21.3671875,31.013986 22.4335938,32.5842339 C23.3476562,33.9129053 24.1445312,35 25.2929688,35 Z"
                fill="#fff"
                fillRule="nonzero"
            />
        </g>
    </svg>
);
